import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  People,
  Inventory,
  AttachMoney,
  Report,
  VerifiedUser,
  AccountBalance,
  Payment,
  ArrowForward,
} from '@mui/icons-material';
import { useGetDashboardStatsQuery } from '../../store/api/adminApi';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: dashboardStats, isLoading, error } = useGetDashboardStatsQuery();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        ダッシュボードデータの読み込みに失敗しました
      </Alert>
    );
  }

  const StatCard = ({ 
    title, 
    value, 
    icon, 
    color = 'primary',
    path 
  }: {
    title: string;
    value: number;
    icon: React.ReactNode;
    color?: string;
    path?: string;
  }) => (
    <Card 
      sx={{ 
        minWidth: 200, 
        margin: 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': path ? {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        } : {},
      }}
    >
      <CardActionArea 
        onClick={() => path && navigate(path)}
        disabled={!path}
        sx={{ height: '100%' }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography color="textSecondary" gutterBottom>
                {title}
              </Typography>
              <Typography variant="h4" component="div">
                {value.toLocaleString()}
              </Typography>
            </Box>
            <Box>
              {icon}
            </Box>
          </Box>
          {path && (
            <Box display="flex" alignItems="center" justifyContent="flex-end" mt={1}>
              <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                詳細を見る <ArrowForward sx={{ fontSize: 14, ml: 0.5 }} />
              </Typography>
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        ダッシュボード (v1.1)
      </Typography>

      <Box display="flex" flexWrap="wrap" sx={{ mb: 3 }}>
        <StatCard
          title="総ユーザー数"
          value={dashboardStats?.overview?.totalUsers || 0}
          icon={<People sx={{ fontSize: 40, color: 'primary.main' }} />}
          color="primary"
          path="/users"
        />
        <StatCard
          title="総商品数"
          value={dashboardStats?.overview?.totalProducts || 0}
          icon={<Inventory sx={{ fontSize: 40, color: 'success.main' }} />}
          color="success"
          path="/products"
        />
        <StatCard
          title="取引数"
          value={dashboardStats?.overview?.totalTransactions || 0}
          icon={<TrendingUp sx={{ fontSize: 40, color: 'warning.main' }} />}
          color="warning"
          path="/bank-transfers"
        />
        <StatCard
          title="売上金額"
          value={dashboardStats?.overview?.totalRevenue || 0}
          icon={<AttachMoney sx={{ fontSize: 40, color: 'error.main' }} />}
          color="error"
          path="/bank-transfers"
        />
      </Box>

      <Box display="flex" flexWrap="wrap" sx={{ mb: 3 }}>
        <StatCard
          title="未対応通報"
          value={dashboardStats?.overview?.pendingReports || 0}
          icon={<Report sx={{ fontSize: 40, color: 'error.main' }} />}
          color="error"
          path="/reports"
        />
        <StatCard
          title="本人確認待ち"
          value={dashboardStats?.overview?.pendingVerifications || 0}
          icon={<VerifiedUser sx={{ fontSize: 40, color: 'primary.main' }} />}
          color="primary"
          path="/users?status=pending_verification"
        />
        <StatCard
          title="銀行振込確認待ち"
          value={dashboardStats?.overview?.pendingBankTransfers || 0}
          icon={<AccountBalance sx={{ fontSize: 40, color: 'secondary.main' }} />}
          color="secondary"
          path="/bank-transfers?status=pending"
        />
        <StatCard
          title="その他決済確認待ち"
          value={dashboardStats?.overview?.pendingOtherPayments || 0}
          icon={<Payment sx={{ fontSize: 40, color: 'warning.main' }} />}
          color="warning"
          path="/bank-transfers?status=pending_other"
        />
      </Box>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        チュートリアル統計
      </Typography>
      <Box display="flex" flexWrap="wrap" sx={{ mb: 3 }}>
        <StatCard
          title="出品希望"
          value={dashboardStats?.overview?.tutorialStats?.sell || 0}
          icon={<Inventory sx={{ fontSize: 40 }} />}
          color="secondary"
        />
        <StatCard
          title="購入希望"
          value={dashboardStats?.overview?.tutorialStats?.buy || 0}
          icon={<TrendingUp sx={{ fontSize: 40 }} />}
          color="info"
        />
        <StatCard
          title="総回答数"
          value={dashboardStats?.overview?.tutorialStats?.total || 0}
          icon={<People sx={{ fontSize: 40 }} />}
          color="success"
        />
      </Box>

    </Box>
  );
};

export default DashboardPage; 